import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { MotiveRefuzService } from 'src/app/platform/nomenclatoare/motive-refuz/motive-refuz.service';
import { CereriService } from '../../cereri.service';
import { helper } from 'src/environments/helper';
import { NotificationService } from 'src/app/services/notification.service';
import { notDefaultValidator } from 'src/app/validators/not-default';

export type MotivRefuzDialogType =
    | 'resetValidare'
    | 'resetInvalidare'
    | 'resetClasare'
    | 'resetInchidere'
    | 'invalidare'
    | 'clasare';

export interface MotivRefuzDialogData {
    type: MotivRefuzDialogType;
    id_cerere: number;
}

const PLACEHOLDER_MOTIV = { id: -1, name: '-- Alegeți --' };
const ALTELE_MOTIV = { id: -2, name: 'Altele' };

const DIALOG_CONFIG: Record<MotivRefuzDialogType, { title: string; text: string }> = {
    resetValidare: { title: 'Resetare validare', text: 'Sunteți sigur că doriți resetarea validării de cerere?' },
    resetInvalidare: { title: 'Resetare invalidare', text: 'Sunteți sigur că doriți resetarea invalidării de cerere?' },
    resetClasare: { title: 'Resetare clasare', text: 'Sunteți sigur că doriți resetarea clasării de cerere?' },
    resetInchidere: { title: 'Resetare închidere', text: 'Sunteți sigur că doriți resetarea închiderii de cerere? Confirmați anularea de închidere a cererii în cadrul modulului de INDOC - Registratură?' },
    invalidare: { title: 'Invalidare cerere', text: 'Sunteți sigur că doriți invalidarea cererii?' },
    clasare: { title: 'Clasare cerere', text: 'Sunteți sigur că doriți clasarea cererii?' },
};

@Component({
    selector: 'app-motiv-refuz-dialog',
    templateUrl: './motiv-refuz-dialog.component.html',
    styleUrls: ['./motiv-refuz-dialog.component.scss']
})
export class MotivRefuzDialogComponent implements OnInit {
    form!: FormGroup;
    motive: any[] = [];
    isLoading = false;
    isSubmitting = false;

    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;

    config = DIALOG_CONFIG[this.data.type];
    helper = helper;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: MotivRefuzDialogData,
        private dialogRef: MatDialogRef<MotivRefuzDialogComponent>,
        private motivRefuzService: MotiveRefuzService,
        private cereriService: CereriService,
        private notificationService: NotificationService,
    ) { }

    ngOnInit(): void {
        this.form = new FormGroup({
            motiv: new FormControl(PLACEHOLDER_MOTIV, [
                Validators.required,
                (ctrl) => ctrl.value?.id === -1 ? { placeholderSelected: true } : null,
            ]),
            motivAltele: new FormControl(''),
        });

        // Când se schimbă motivul, adăugăm/scoatem validarea pe motivAltele
        this.form.get('motiv')!.valueChanges.subscribe((val) => {
            const alteleCtrl = this.form.get('motivAltele')!;
            if (val?.id === -2) {
                alteleCtrl.setValidators([Validators.required]);
            } else {
                alteleCtrl.clearValidators();
                alteleCtrl.setValue('');
            }
            alteleCtrl.updateValueAndValidity();
        });

        this.loadMotive();
    }

    get isAlteleSelected(): boolean {
        return this.form.get('motiv')?.value?.id === -2;
    }

    loadMotive(): void {
        this.isLoading = true;
        this.motivRefuzService
            .getActiveMotiveRefuz()
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res: any) => {
                    this.motive = [
                        PLACEHOLDER_MOTIV,
                        ...(res?.data ?? []),
                        ALTELE_MOTIV,
                    ];
                },
                error: (err) => {
                    let msg;
                    try {
                        msg = err.error.errors.message[0];
                    } catch {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService
                        .warningSwal(this.errorTitle, msg, this.errorIcon)
                        .then(() => this.close());
                },
            });
    }

    get selectedMotivText(): string {
        const motiv = this.form.get('motiv')?.value;
        if (motiv?.id === -2) {
            return this.form.get('motivAltele')?.value ?? '';
        }
        return motiv?.name ?? '';
    }

    submit(): void {
        if (this.form.invalid) return;

        this.isSubmitting = true;
        const payload = { motiv: this.selectedMotivText };
        const id = this.data.id_cerere;

        this.resolveRequest(id, payload)
            .pipe(finalize(() => (this.isSubmitting = false)))
            .subscribe({
                next: () => this.dialogRef.close({ success: true }),
                error: (err) => {
                    let msg;
                    try {
                        msg = err.error.errors.message[0];
                    } catch {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(this.errorTitle, msg, this.errorIcon);
                },
            });
    }

    private resolveRequest(id: number, payload: { motiv: string }) {
        switch (this.data.type) {
            case 'resetValidare': return this.cereriService.resetValidareCerere(id, payload);
            case 'resetInvalidare': return this.cereriService.resetinValidareCerere(id, payload);
            case 'resetClasare': return this.cereriService.resetClasareCerere(id, payload);
            case 'resetInchidere': return this.cereriService.resetInchidereCerere(id, payload);
            case 'invalidare': return this.cereriService.invalidateCerere(id, payload);
            case 'clasare': return this.cereriService.anulareCerere(id, payload);
        }
    }

    compareById = (a: any, b: any) => a?.id === b?.id;

    close(): void {
        this.dialogRef.close({ success: false });
    }
}